import { useConversationSkillsQuery } from "#features/conversations/api/useConversationSkillsQuery.ts";
import { localizeSkill, toSkillLanguage } from "#features/conversations/components/conversation-input/skillSearch.ts";
import { useFeatureFlag } from "#hooks/useFeatureFlag.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useMemo } from "react";

// What a skill token and its hover card show, in the user's language.
export interface SkillDisplay {
    title: string;
    description: string;
}

// Each known skill's display, by slug. Empty while SlashCommandPicker is off, so "/slug" stays text then, in the composer
// and in sent messages alike.
export const useSkillDisplays = (): ReadonlyMap<string, SkillDisplay> => {
    const { i18n } = useSharedTranslation();
    const { hasFeatureFlag } = useFeatureFlag();
    const hasSkillTokens = hasFeatureFlag("SlashCommandPicker");
    const skills = useConversationSkillsQuery(hasSkillTokens);
    const skillLanguage = toSkillLanguage(i18n.language);

    // A disabled query still returns skills another surface cached, so the flag gates the tokens itself.
    return useMemo(
        () => new Map(hasSkillTokens
            ? skills.map(skill => {
                const { title, description } = localizeSkill(skill, skillLanguage);

                return [skill.slug, { title, description }] as const;
            })
            : []),
        [hasSkillTokens, skills, skillLanguage]
    );
};
